import classNames from 'classnames'
import {memo, useCallback} from 'react'
import type {MouseEvent} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'

import {getHrefWithQueryParams} from '../../../../../../../../../routes'
import {stringifyId} from '../../../../../../../../../types'
import type {TestOccurrenceId} from '../../../../../../../../../types'
import {isEventWithSpecialKeyPressed} from '../../../../../../../../../utils/keys'
import RouterLink from '../../../../../../../../common/RouterLink/RouterLink'
import TruncateHtml from '../../../../../../../../common/TruncateHtml/TruncateHtml'
import {
  getTestOccurrence,
  getTestOccurrenceNewFailure,
  getTestOccurrenceNextFixed,
} from '../../../../../../../../packages/Tests/Tests.selectors'
import {TestStatus} from '../../../../../../../../packages/Tests/Tests.types'
import type {TestScope} from '../../../../../../../../packages/Tests/Tests.types'
import {getTestScope, trimAndRemoveColon} from '../../../../../../../../packages/Tests/Tests.utils'

import styles from './TestName.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  testOccurrenceId?: TestOccurrenceId
  withPartsLink?: boolean
  withTrancateName?: boolean
}
let timer: number
const CLICK_TIMEOUT = 200
const MAX_NUMBER_OF_VISIBLE_LINES = 3

function TestName({testOccurrenceId, withPartsLink, withTrancateName}: Props) {
  const location = useLocation()
  const testOccurrence = useAppSelector(state => getTestOccurrence(state, testOccurrenceId))
  const newFailure = useAppSelector(state => getTestOccurrenceNewFailure(state, testOccurrenceId))
  const nextFixed = useAppSelector(state => getTestOccurrenceNextFixed(state, testOccurrenceId))
  const {name, test, status, muted} = testOccurrence ?? {}
  const {testClass, testNameWithParameters, testSuite, testPackage} = test?.parsedTestName ?? {}
  const formattedTestSuite =
    testPackage != null && testPackage !== '' ? testSuite : trimAndRemoveColon(testSuite ?? '')
  const scope: TestScope = {
    suite: testSuite,
    package: testPackage,
    class: testClass,
  }
  const navigate = useNavigate()
  const onClickLink = useCallback(
    (event: MouseEvent<any>) => {
      if (isEventWithSpecialKeyPressed(event)) {
        return
      }

      const {currentTarget, detail} = event
      event.stopPropagation()
      event.preventDefault()
      clearTimeout(timer)

      if (detail === 1) {
        timer = window.setTimeout(() => {
          const selection = window.getSelection()
          if (selection == null || selection.toString().length === 0) {
            navigate(currentTarget.getAttribute('href') ?? '')
          }
        }, CLICK_TIMEOUT)
      }
    },
    [navigate],
  )

  const getNamePart = (
    scopeKey: 'class' | 'name' | 'suite' | 'package',
    value: string | null | undefined,
    withLink = false,
    withSpace = false,
  ) => {
    const span = <span className={styles[scopeKey]}>{`${withSpace ? ' ' : ''}${value}`}</span>
    return value != null && value.length > 0 ? (
      withLink ? (
        <RouterLink
          to={getHrefWithQueryParams(location, null, prevParams => ({
            ...prevParams,
            ...(scopeKey === 'name'
              ? {
                  expandedTest:
                    prevParams.expandedTest === stringifyId(testOccurrenceId)
                      ? null
                      : stringifyId(testOccurrenceId),
                }
              : {
                  ...getTestScope(scopeKey, scope),
                  page: null,
                  expandedTest: null,
                }),
          }))}
          className={styles.link}
          title={scopeKey !== 'name' ? `Filter by "${value}"` : undefined}
          onClick={onClickLink}
          draggable="false"
          data-test-build-test-name-part={scopeKey}
        >
          {span}
        </RouterLink>
      ) : (
        span
      )
    ) : null
  }

  return (
    <TruncateHtml
      className={classNames(styles.name, {
        [styles.withInteractiveLinks]: withPartsLink,
        [styles.newFailure]: newFailure === true && status === TestStatus.FAILURE && !muted,
        [styles.fixed]: nextFixed != null,
      })}
      lineHeight={16}
      clamp={withTrancateName ? MAX_NUMBER_OF_VISIBLE_LINES : null}
      withToggleButton={false}
    >
      {testNameWithParameters ? (
        <>
          {getNamePart('class', testClass, withPartsLink)}
          {testClass ? <span className={styles.separator}>{'.'}</span> : null}
          {getNamePart('name', testNameWithParameters, true)}
          {getNamePart('suite', formattedTestSuite, withPartsLink, true)}
          {getNamePart('package', testPackage, withPartsLink, true)}
        </>
      ) : (
        getNamePart('name', name, true)
      )}
    </TruncateHtml>
  )
}

export default memo<Props>(TestName)
